import { useEffect, useMemo, useState } from 'react'

import { useUserRoles } from './useUserRoles'
import { hasAnyRole } from '../auth/roles'
import { MenuItem } from '../types/types'

interface UseMenuResult {
  menu: MenuItem[]
  isLoading: boolean
}

let cachedMenu: MenuItem[] | null = null
let pending: Promise<MenuItem[]> | null = null

// Menu definitions (and their icon imports) are loaded lazily and cached after the first load
const loadMenu = (): Promise<MenuItem[]> => {
  if (cachedMenu) return Promise.resolve(cachedMenu)
  pending ??= import('../types/Menu').then(({ menu }) => {
    cachedMenu = menu
    return menu
  })
  return pending
}

// Drops items the user's roles don't allow, and any section left with nothing in it
export const filterMenuByRoles = (items: MenuItem[], roles: string[]): MenuItem[] =>
  items
    .filter(item => hasAnyRole(roles, item.roles))
    .map(item => (item.children ? { ...item, children: filterMenuByRoles(item.children, roles) } : item))
    .filter(item => !item.children || item.children.length > 0)

export const useMenu = (): UseMenuResult => {
  const { roles } = useUserRoles()
  const [menu, setMenu] = useState<MenuItem[]>(cachedMenu ?? [])
  const [isLoading, setIsLoading] = useState<boolean>(!cachedMenu)

  useEffect(() => {
    if (cachedMenu) return
    let cancelled = false
    loadMenu()
      .then(loaded => {
        if (!cancelled) setMenu(loaded)
      })
      .catch(() => {
        pending = null
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const visibleMenu = useMemo(() => filterMenuByRoles(menu, roles), [menu, roles])

  return { menu: visibleMenu, isLoading }
}
